import _ from 'lodash';
import { SelectionAreaWrapper } from '@/shared/ui';
import { eventOffsetPreviewStrip } from '@/entities/strip';
import EventPreviewStrip from '@/widgets/beats/ui/eventPreviewStrip';
import { ID_DIV_STRIP_ARTIFACT } from '@/widgets/beats/ui/helper';
import useStripEvents from './useStripEvents';

export type StripEventsProps = {
  studyId: string;
  profileId: string;
  ecgDataMap: any;
  timezoneOffset: number;
  startReport: any;
};

/**
 * Lists the artifact-area event strips as selectable ECG thumbnails. Reads holter events, loading state, thumbnail display settings and selection handlers from useStripEvents, then maps each event to an EventPreviewStrip inside a selection-area wrapper that marks the active strip and the rubber-band selection.
 *
 * @param props - Strip list props with the ECG data map, timezone offset and report start.
 * @returns The rendered selectable grid of event strip thumbnails.
 */
const StripEvents = ({
  studyId,
  profileId,
  ecgDataMap,
  timezoneOffset,
  startReport,
}: StripEventsProps) => {
  const {
    holterEvents,
    isLoadingStrip,
    selectedStrip,
    setSelectedEvent,
    selectionAreaWrapperRef,
    handleClickSelectEvent,
    isHasSelected,
    highPassThumbnail,
    gainThumbnail,
    channelsThumbnail,
    sizeThumbnailConfig,
  } = useStripEvents({ studyId, profileId, timezoneOffset });

  return (
    <SelectionAreaWrapper
      ref={selectionAreaWrapperRef}
      id={ID_DIV_STRIP_ARTIFACT}
      className="strips-container --min-width-strip-event"
      selectables=".single-strip-event"
      handleSetSelected={setSelectedEvent}
      defaultSelectedId={selectedStrip?.id as any}
      isNoData={holterEvents?.length === 0}
      isLoading={isLoadingStrip}
    >
      {(selected) => (
        <div className="strips-img-container">
          {_.map(holterEvents, (event: any, index) => (
            <EventPreviewStrip
              key={event.id}
              index={index}
              id={`beat-${event.id}`}
              isActive={event.id === selectedStrip?.id}
              isSelected={selected.has(`beat-${event.id}`) && isHasSelected}
              event={event}
              ecgDataMap={ecgDataMap}
              highPass={highPassThumbnail}
              gain={gainThumbnail}
              channels={channelsThumbnail}
              width={sizeThumbnailConfig.width}
              height={sizeThumbnailConfig.height}
              customOffset={eventOffsetPreviewStrip}
              timezoneOffset={timezoneOffset}
              startReport={startReport}
              onClickSelectEvent={handleClickSelectEvent}
            />
          ))}
        </div>
      )}
    </SelectionAreaWrapper>
  );
};

export default StripEvents;
